import { useNavigate } from 'react-router-dom';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  ROUTER_PATHS,
  TABLE_CONSTANTS_COLUMS,
  hasAllPermissions,
  hasPermission,
} from '@/shared';
import {
  useDeletePromocion,
  useFetchAllPromociones,
} from '@/shared/actions/erp';
import { CustomSearch, SingleTableBoxScene } from '@/shared/components/ui';
import GeneralTable from '@/shared/components/ui/GeneralTable';
import {
  useCheckPermissions,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared/hooks';
import { IPromocion } from '@/shared/interfaces/erp/promociones';
import { useUiConfirmModalStore } from '@/store/ui';
import { SAVE_PROMOCION_PEMISSIONS } from '../../shared/constants';
import { useColumnsPromociones } from '../../shared/hooks';



export type PromocionesPageProps = {};

const PromocionesPage: React.FC<PromocionesPageProps> = () => {
  useCheckPermissions(PermissionsEnum.api_view_promocion);
  const returnUrlPromocionesPage =
    ROUTER_PATHS.administration.promocionesNav;
  const navigate = useNavigate();

  // server side filters - colums table
  const { filterObject, columnFilters, setColumnFilters } =
    useTableServerSideFiltering();

  ///* global state -----------------------
  const setConfirmDialog = useUiConfirmModalStore(s => s.setConfirmDialog);
  const setConfirmDialogIsOpen = useUiConfirmModalStore(
    s => s.setConfirmDialogIsOpen
  );

  ///* table -----------------------
  const {
    globalFilter,
    pagination,
    searchTerm,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  ///* fetch data -----------------------
  const {
    data: promocionesRes,
    isLoading,
    isRefetching,
  } = useFetchAllPromociones({
    page: pageIndex + 1,
    page_size: pageSize,
    nombre: searchTerm,
    ...filterObject,
    filterByState: false,
  });

  ///* mutations -----------------------
  const deleteProductMutation = useDeletePromocion();

  ///* handlers -----------------------
  const onEdit = (original: IPromocion) => {
    navigate(`${returnUrlPromocionesPage}/editar/${original.id_promocion}`);
  };
  const onDelete = (original: IPromocion) => {
    setConfirmDialog({
      isOpen: true,
      subtitle: '¿Está seguro que desea eliminar la Promoción seleccionada?',
      title: 'Confirmar',
      onConfirm: () => {
        deleteProductMutation.mutate(original.id_promocion!);
      },
      onClose() {
        setConfirmDialogIsOpen(false);
      },
    });
  };

  ///* columns -----------------------
  const { promocionesColumns } = useColumnsPromociones();

  return (
    <SingleTableBoxScene
      title="Promociones"
      urlRedirect={`${returnUrlPromocionesPage}/crear`}
      enableRedirect={hasPermission(PermissionsEnum.api_add_promocion)}
    >
      <CustomSearch
        onChange={onChangeFilter}
        value={globalFilter}
        text="por nombre"
      />

      <GeneralTable<IPromocion>
        columns={promocionesColumns}
        data={promocionesRes?.data?.data || []}
        //
        isLoading={isLoading}
        isRefetching={isRefetching}
        // // search
        enableGlobalFilter={false}
        // // filters - llega al back
        enableManualFiltering={true}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        // // pagination
        pagination={pagination}
        onPaging={setPagination}
        rowCount={promocionesRes?.data?.count}
        // // actions ========
        enableColumnActions={hasAllPermissions([
          ...SAVE_PROMOCION_PEMISSIONS,
          PermissionsEnum.api_change_ciudad,
        ])}
        actionsColumnSize={TABLE_CONSTANTS_COLUMS.ACTIONCOLUMN_WIDTH}
        // crud
        canEdit={hasAllPermissions([
          ...SAVE_PROMOCION_PEMISSIONS,
          PermissionsEnum.api_change_plan,
        ])}
        onEdit={onEdit}
        canDelete={false}
        onDelete={onDelete}
      />
    </SingleTableBoxScene>
  );
};

export default PromocionesPage;
